/**
 * B6 (E-2.J.9-01): Email HTML Renderer
 *
 * Converts email components to email-safe HTML.
 * Table layout for Outlook compatibility.
 * Inline styles (no external CSS in email).
 * Images with alt text for "images off" mode.
 * Responsive via media queries where supported.
 */

import type { EmailComponentType } from "./email-components.js";

export interface EmailComponent {
  type: EmailComponentType;
  props: Record<string, unknown>;
}

export interface RawEmailRow {
  rawHtml: string;
}

export type EmailRenderableNode = EmailComponent | RawEmailRow;

export interface EmailRenderOptions {
  maxWidth?: number;        // default 600
  backgroundColor?: string; // default #f4f4f4
  contentBg?: string;       // default #ffffff
  fontFamily?: string;      // default Arial, sans-serif
}

const DEFAULT_OPTIONS: Required<EmailRenderOptions> = {
  maxWidth: 600,
  backgroundColor: "#f4f4f4",
  contentBg: "#ffffff",
  fontFamily: "Arial, Helvetica, sans-serif",
};

/**
 * Render an array of email components to email-safe HTML.
 * Output is a complete HTML document ready for email sending.
 */
export function renderEmailHtml(
  components: EmailRenderableNode[],
  options: EmailRenderOptions = {},
): string {
  const opts = { ...DEFAULT_OPTIONS, ...options };

  const bodyContent = components
    .map((component) => isRawEmailRow(component) ? component.rawHtml : renderComponent(component, opts))
    .join("\n");

  return `<!DOCTYPE html>
<html lang="en" xmlns="http://www.w3.org/1999/xhtml" xmlns:v="urn:schemas-microsoft-com:vml">
<head>
<meta charset="utf-8">
<meta name="viewport" content="width=device-width, initial-scale=1.0">
<meta http-equiv="X-UA-Compatible" content="IE=edge">
<title></title>
<style type="text/css">
  body { margin: 0; padding: 0; -webkit-text-size-adjust: 100%; -ms-text-size-adjust: 100%; }
  table { border-collapse: collapse; mso-table-lspace: 0pt; mso-table-rspace: 0pt; }
  img { border: 0; height: auto; line-height: 100%; outline: none; text-decoration: none; }
  @media only screen and (max-width: 620px) {
    .email-container { width: 100% !important; max-width: 100% !important; }
    .fluid { max-width: 100% !important; height: auto !important; }
  }
</style>
</head>
<body style="margin:0;padding:0;background-color:${opts.backgroundColor};font-family:${opts.fontFamily};">
<table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="background-color:${opts.backgroundColor};">
<tr><td align="center" style="padding:20px 0;">
<table role="presentation" class="email-container" width="${opts.maxWidth}" cellpadding="0" cellspacing="0" style="max-width:${opts.maxWidth}px;background-color:${opts.contentBg};border-radius:4px;">
${bodyContent}
</table>
</td></tr>
</table>
</body>
</html>`;
}

function isRawEmailRow(value: EmailRenderableNode): value is RawEmailRow {
  return "rawHtml" in value;
}

function renderComponent(component: EmailComponent, opts: Required<EmailRenderOptions>): string {
  const p = component.props;

  switch (component.type) {
    case "email_header": {
      const level = String(p.level ?? "h1");
      const size = level === "h1" ? 28 : level === "h2" ? 22 : 18;
      return `<tr><td style="padding:20px 30px;text-align:${String(p.align ?? "center")};">
<${level} style="margin:0;font-size:${size}px;color:${String(p.color ?? "#1a1a1a")};font-family:${opts.fontFamily};">${escapeHtml(String(p.text ?? ""))}</${level}>
</td></tr>`;
    }

    case "email_text":
      return `<tr><td style="padding:10px 30px;font-size:${String(p.fontSize ?? 16)}px;line-height:${String(p.lineHeight ?? 1.6)};color:${String(p.color ?? "#333333")};font-family:${opts.fontFamily};">
${escapeHtml(String(p.text ?? ""))}
</td></tr>`;

    case "email_image":
      return `<tr><td style="padding:10px 30px;text-align:${String(p.align ?? "center")};">
<img src="${escapeHtml(String(p.src ?? ""))}" alt="${escapeHtml(String(p.alt ?? ""))}" width="${String(p.width ?? opts.maxWidth - 60)}" class="fluid" style="display:block;max-width:100%;height:auto;">
</td></tr>`;

    case "email_button": {
      const bg = String(p.backgroundColor ?? "#0066cc");
      const tc = String(p.textColor ?? "#ffffff");
      const br = String(p.borderRadius ?? 4);
      const pad = String(p.padding ?? "12px 24px");
      return `<tr><td style="padding:20px 30px;text-align:center;">
<!--[if mso]><v:roundrect xmlns:v="urn:schemas-microsoft-com:vml" href="${escapeHtml(String(p.href ?? "#"))}" style="height:44px;width:200px;v-text-anchor:middle;" arcsize="10%" strokecolor="${bg}" fillcolor="${bg}"><w:anchorlock/><center style="color:${tc};font-family:${opts.fontFamily};font-size:16px;font-weight:bold;">${escapeHtml(String(p.text ?? "Click"))}</center></v:roundrect><![endif]-->
<!--[if !mso]><!--><a href="${escapeHtml(String(p.href ?? "#"))}" style="display:inline-block;padding:${pad};background-color:${bg};color:${tc};border-radius:${br}px;text-decoration:none;font-weight:bold;font-family:${opts.fontFamily};font-size:16px;">${escapeHtml(String(p.text ?? "Click"))}</a><!--<![endif]-->
</td></tr>`;
    }

    case "email_divider":
      return `<tr><td style="padding:${String(p.margin ?? "20px 0")};">
<hr style="border:0;border-top:${String(p.height ?? 1)}px solid ${String(p.color ?? "#e0e0e0")};margin:0 30px;">
</td></tr>`;

    case "email_footer":
      return `<tr><td style="padding:20px 30px;font-size:${String(p.fontSize ?? 12)}px;color:${String(p.color ?? "#999999")};text-align:center;font-family:${opts.fontFamily};">
${escapeHtml(String(p.companyName ?? ""))}<br>
${escapeHtml(String(p.address ?? ""))}<br>
<a href="{{unsubscribe_url}}" style="color:${String(p.color ?? "#999999")};">${escapeHtml(String(p.unsubscribeText ?? "Unsubscribe"))}</a>
</td></tr>`;

    case "email_social": {
      const networks = Array.isArray(p.networks) ? p.networks : [];
      const size = String(p.iconSize ?? 32);
      const icons = (networks as string[]).map((n) =>
        `<a href="{{social_${n}_url}}" style="display:inline-block;margin:0 4px;"><img src="{{social_${n}_icon}}" alt="${n}" width="${size}" height="${size}" style="display:block;"></a>`,
      ).join("");
      return `<tr><td style="padding:15px 30px;text-align:${String(p.align ?? "center")};">${icons}</td></tr>`;
    }

    case "email_product_card":
      return `<tr><td style="padding:15px 30px;">
<table role="presentation" width="100%" cellpadding="0" cellspacing="0">
<tr>
<td width="40%" style="vertical-align:top;"><img src="${escapeHtml(String(p.imageSrc ?? ""))}" alt="${escapeHtml(String(p.name ?? ""))}" width="200" class="fluid" style="display:block;max-width:100%;height:auto;"></td>
<td width="60%" style="vertical-align:top;padding-left:15px;">
<p style="margin:0 0 5px;font-weight:bold;font-size:16px;color:#1a1a1a;">${escapeHtml(String(p.name ?? ""))}</p>
<p style="margin:0 0 5px;font-size:14px;color:#666;">${escapeHtml(String(p.description ?? ""))}</p>
<p style="margin:0 0 10px;font-size:18px;font-weight:bold;color:#1a1a1a;">${escapeHtml(String(p.price ?? ""))}</p>
<a href="${escapeHtml(String(p.ctaHref ?? "#"))}" style="display:inline-block;padding:8px 16px;background-color:#0066cc;color:#ffffff;border-radius:4px;text-decoration:none;font-size:14px;">${escapeHtml(String(p.ctaText ?? "View"))}</a>
</td></tr></table>
</td></tr>`;

    default:
      return "";
  }
}

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}
